/**
 * AuthLayout (V35.7) — Layout split-screen premium pour toutes les pages d'authentification.
 *
 * - Hero gauche : storytelling + branding personnalisable
 * - Formulaire droite : passé en `children`
 * - Variants : "default" (bleu nuit COPPAM) ou "admin" (slate sombre admin)
 *
 * Usage :
 *   <AuthLayout
 *     variant="default"
 *     badge="Espace membres"
 *     title="Bienvenue sur"
 *     titleHighlight="COPPAM"
 *     description="..."
 *     features={[...]}
 *   >
 *     {formContent}
 *   </AuthLayout>
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Mail, Sparkles, ArrowLeft } from 'lucide-react';

const VARIANTS = {
  default: {
    bgGradient: 'from-slate-950 via-blue-950 to-indigo-950',
    heroGradient: 'from-[#0B3C5D] via-[#0a1e3f] to-[#091a36]',
    accentColor: 'amber-300',
    badgeBg: 'bg-amber-400/10 border-amber-300/30 text-amber-200',
    badgePulse: 'bg-amber-300',
    highlightText: 'text-amber-300',
    blob1: 'bg-blue-500/20',
    blob2: 'bg-indigo-500/20',
    blob3: 'bg-purple-500/10',
  },
  admin: {
    bgGradient: 'from-zinc-950 via-slate-950 to-zinc-900',
    heroGradient: 'from-zinc-900 via-slate-900 to-zinc-950',
    accentColor: 'red-300',
    badgeBg: 'bg-red-500/10 border-red-400/30 text-red-200',
    badgePulse: 'bg-red-400',
    highlightText: 'text-red-300',
    blob1: 'bg-red-500/15',
    blob2: 'bg-orange-500/10',
    blob3: 'bg-zinc-500/10',
  },
};

export default function AuthLayout({
  variant = 'default',
  badge,
  badgeIcon,
  title,
  titleHighlight,
  description,
  features = [],
  trustSignals = [
    { icon: Shield, label: 'Politique de confidentialité', to: '/politique-rgpd' },
    { icon: CheckCircle, label: 'Conditions d’utilisation', to: '/conditions-generales' },
    { icon: Mail, label: 'Contact et assistance', to: '/contact' },
  ],
  showHomeLink = true,
  children,
}) {
  const v = VARIANTS[variant] || VARIANTS.default;
  const BadgeIcon = badgeIcon;

  return (
    <div className={`min-h-screen relative overflow-hidden bg-gradient-to-br ${v.bgGradient}`}>
      {/* Décor global */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className={`absolute -top-32 -left-32 w-96 h-96 ${v.blob1} rounded-full blur-3xl`} />
        <div className={`absolute top-1/3 -right-32 w-[28rem] h-[28rem] ${v.blob2} rounded-full blur-3xl`} />
        <div className={`absolute -bottom-32 left-1/2 w-[32rem] h-[32rem] ${v.blob3} rounded-full blur-3xl`} />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      <div className="relative min-h-screen flex items-center justify-center px-4 pb-4 pt-24 sm:px-6 sm:pb-6 lg:px-8 lg:pb-8">
        {showHomeLink && (
          <Link
            to="/"
            data-testid="auth-back-home-btn"
            className="absolute top-4 left-4 sm:top-6 sm:left-6 z-20 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-medium backdrop-blur-sm transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l&apos;accueil
          </Link>
        )}
        <div className="w-full max-w-6xl grid lg:grid-cols-[1.05fr_1fr] gap-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-black/40 ring-1 ring-white/20 overflow-hidden">
          {/* === HERO LEFT === */}
          <aside
            className={`relative bg-gradient-to-br ${v.heroGradient} p-8 sm:p-10 lg:p-12 text-white flex flex-col justify-between lg:min-h-[640px]`}
          >
            <div className="absolute inset-0 pointer-events-none opacity-25" aria-hidden="true">
              <div className="absolute top-10 right-10 w-32 h-32 border border-white/30 rounded-full" />
              <div className="absolute top-20 right-20 w-20 h-20 border border-white/20 rounded-full" />
              <div className="absolute bottom-32 left-10 w-40 h-40 border-2 border-white/15 rounded-2xl rotate-12" />
            </div>

            <div className="relative">
              {/* Logo / Brand */}
              {showHomeLink && (
                <Link to="/" className="inline-flex items-center gap-2 mb-8 group">
                  <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-sm group-hover:bg-white/20 transition-all">
                    <Sparkles className={`w-5 h-5 ${v.highlightText}`} />
                  </div>
                  <span className="font-bold text-white text-lg tracking-tight">O&apos;SCOP Outremer</span>
                </Link>
              )}

              {/* Badge */}
              {badge && (
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wider mb-6 ${v.badgeBg}`}>
                  <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${v.badgePulse}`} />
                  {BadgeIcon && <BadgeIcon className="w-3.5 h-3.5" />}
                  {badge}
                </div>
              )}

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
                {title}{' '}
                {titleHighlight && <span className={v.highlightText}>{titleHighlight}</span>}
              </h1>

              {description && (
                <p className="text-white/85 text-base sm:text-lg leading-relaxed mb-6 max-w-md">
                  {description}
                </p>
              )}

              {/* Features list */}
              {features.length > 0 && (
                <ul className="space-y-2 mt-6">
                  {features.map((f, i) => {
                    const Icon = f.icon || CheckCircle;
                    return (
                      <li key={i} className="flex items-start gap-3 text-white/90 text-sm">
                        <span className="flex-shrink-0 w-6 h-6 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-sm">
                          <Icon className={`w-3.5 h-3.5 ${v.highlightText}`} />
                        </span>
                        <span>{f.label}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Informations utiles et liens vers les conditions applicables */}
            <div className="relative mt-10 pt-6 border-t border-white/10">
              <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold mb-3">
                Informations utiles
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-white/70 text-xs">
                {trustSignals.map((ts, i) => {
                  const TsIcon = ts.icon;
                  return (
                    <div key={i} className="flex items-center gap-1.5">
                      <TsIcon className="w-3.5 h-3.5 text-emerald-300" />
                      {ts.to ? <Link to={ts.to} className="underline underline-offset-2 hover:text-white">{ts.label}</Link> : <span>{ts.label}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* === FORM RIGHT === */}
          <section className="p-8 sm:p-10 lg:p-12 flex flex-col justify-center">{children}</section>
        </div>
      </div>
    </div>
  );
}
